import React, { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { Check, Copy, Download, ImageOff, QrCode } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatCurrency } from "@/lib/format";
import pixService from "@/services/pixService";
import { showError } from "@/store/errorStore";
import { PixStatusBadge, getPixTitle } from "./PixPaymentCard";

/**
 * QR Code e PIX Copia e Cola de uma cobrança.
 * Usa a imagem gerada pela API (qrcodeUrl); sem ela, desenha o QR a partir
 * do payload. Com `pix.isStatic` mostra o QR do balcão (sem valor fixo).
 */
const PixQrCodeModal = ({ open, onOpenChange, pix }) => {
  const [copied, setCopied] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const svgRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCopied(false);
      setImageFailed(false);
    }
  }, [open, pix]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!pix) return null;

  const qrCodeUrl = pix.qrcodeUrl || pix.qrcode || pix.imagemQrcode;
  const payload = pix.payload || pix.pixCopiaECola;
  const useImage = Boolean(qrCodeUrl) && !imageFailed;
  const paid = pixService.isPaid(pix);
  const fileName = pix.isStatic ? "qrcode-pix-balcao" : `qrcode-pix-${pix.txid || "cobranca"}`;

  const handleCopy = async () => {
    try {
      await pixService.copyToClipboard(payload);
      setCopied(true);
    } catch (error) {
      showError("Não foi possível copiar. Selecione o código e copie manualmente.", "Copiar");
    }
  };

  const download = (href, extension) => {
    const link = document.createElement("a");
    link.href = href;
    link.download = `${fileName}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = () => {
    if (useImage) {
      download(qrCodeUrl, "png");
      return;
    }
    const svg = svgRef.current;
    if (!svg) return;
    const blob = new Blob([new XMLSerializer().serializeToString(svg)], {
      type: "image/svg+xml",
    });
    const url = URL.createObjectURL(blob);
    download(url, "svg");
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md gap-4 overflow-y-auto rounded-2xl p-5">
        <DialogHeader className="flex-row items-center gap-2 space-y-0 pr-8 text-left">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <QrCode className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <DialogTitle className="font-display text-lg text-primary">
              {pix.isStatic ? "QR Code do Balcão" : "QR Code PIX"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {pix.isStatic
                ? "O cliente digita o valor no app do banco"
                : "Escaneie o QR Code ou copie o PIX Copia e Cola"}
            </DialogDescription>
          </div>
        </DialogHeader>

        {/* Resumo da cobrança */}
        <div className="flex items-center justify-between gap-3 rounded-xl bg-surface p-3.5">
          <div className="min-w-0">
            <span className="block truncate font-display text-base font-bold text-primary">
              {pix.isStatic ? pix.name : getPixTitle(pix)}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {pix.isStatic
                ? `Chave ${pixService.getPixKeyType(pix.pixKey)}: ${pix.pixKey}`
                : pix.name || "Cliente não informado"}
            </span>
          </div>
          <div className="flex flex-shrink-0 flex-col items-end gap-1">
            <span className="font-display text-xl font-bold tracking-tight text-primary">
              {pix.isStatic ? "Valor livre" : formatCurrency(pixService.getAmount(pix))}
            </span>
            {!pix.isStatic && <PixStatusBadge paid={paid} />}
          </div>
        </div>

        {/* QR Code */}
        {useImage || payload ? (
          <div className="flex flex-col items-center gap-2">
            <div className="rounded-2xl border bg-white p-4 shadow-soft">
              {useImage ? (
                <img
                  src={qrCodeUrl}
                  alt="QR Code PIX"
                  className="h-56 w-56 object-contain"
                  onError={() => setImageFailed(true)}
                />
              ) : (
                <QRCode
                  ref={svgRef}
                  value={payload}
                  size={224}
                  fgColor="#1c1c16"
                  title="QR Code PIX"
                />
              )}
            </div>
            <button
              type="button"
              onClick={handleDownload}
              className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >
              <Download className="h-3.5 w-3.5" />
              Baixar QR Code
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-8 text-center">
            <ImageOff className="mb-2 h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              Nenhum QR Code ou código PIX disponível para este registro.
            </p>
          </div>
        )}

        {/* PIX Copia e Cola */}
        {payload && (
          <div className="flex flex-col rounded-xl bg-surface p-3">
            <span className="mb-1 text-[11px] font-semibold text-muted-foreground">
              PIX Copia e Cola
            </span>
            <code className="max-h-24 overflow-y-auto break-all rounded-lg bg-card p-2 font-mono text-[11px] leading-relaxed text-foreground">
              {payload}
            </code>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 pt-1">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="flex h-10 items-center justify-center rounded-full bg-surface-container px-3 text-[13px] font-semibold text-foreground transition-colors hover:bg-surface-high"
          >
            Fechar
          </button>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!payload}
            className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-primary px-3 text-[13px] font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-brand-dark disabled:opacity-50"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copiado!" : "Copiar Código"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PixQrCodeModal;
